/**
 * Shared campaign form — one view, grouped into sections, fields by dialing type.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CaretUpDown, Check, Plus } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  DIAL_STATUS_OPTIONS,
  COMMON_TIMEZONES,
  fieldVisibility,
  normalizeAgentCallRouting,
  normalizeAgentLevelRouting,
  normalizeAgentSession,
  visibleInboundGroups,
  selectedOpensipsIds,
  selectedMediaServerIds,
} from "@/lib/campaignForm";
import FailoverServerMultiSelect from "@/components/FailoverServerMultiSelect";

function tid(prefix, name) {
  if (name === "campaign-type") {
    return prefix === "edit-campaign" ? "edit-campaign-type" : "campaign-type";
  }
  if (name === "pd-select") {
    return prefix === "edit-campaign" ? "edit-campaign-pd-select" : "campaign-pd-select";
  }
  if (name === "name") {
    return prefix === "edit-campaign" ? "edit-campaign-name" : "campaign-name";
  }
  return `${prefix}-${name}`;
}

function TimezoneCombobox({ value, onChange, timezones, testid }) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => {
    const base = Array.isArray(timezones) && timezones.length ? timezones : COMMON_TIMEZONES;
    const list = [...base];
    const cur = (value || "").trim();
    if (cur && !list.includes(cur)) list.unshift(cur);
    return list;
  }, [timezones, value]);
  const display = (value || "").trim() || "Select timezone…";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3"
        >
          <span className="truncate">{display}</span>
          <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search timezone…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__ clear timezone"
                data-testid={`${testid}-option-none`}
                onSelect={() => { onChange(""); setOpen(false); }}
              >
                <Check className={`mr-2 h-3.5 w-3.5 ${!(value || "").trim() ? "opacity-100" : "opacity-0"}`} />
                <span className="text-muted-foreground">Account default</span>
              </CommandItem>
              {zones.map((z) => (
                <CommandItem
                  key={z}
                  value={z}
                  data-testid={`${testid}-option-${z.replace(/[^a-zA-Z0-9_-]/g, "_")}`}
                  onSelect={() => { onChange(z); setOpen(false); }}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${(value || "") === z ? "opacity-100" : "opacity-0"}`} />
                  {z}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Multi-select dropdown — several inbound groups per campaign; groups stay selectable across campaigns. */
function InboundGroupsMultiSelect({ groups, selectedIds, onChange, testid, emptyHint }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => (Array.isArray(selectedIds) ? selectedIds : []),
    [selectedIds],
  );
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const byId = useMemo(() => {
    const m = new Map();
    (groups || []).forEach((g) => m.set(g.id, g));
    return m;
  }, [groups]);

  const display = useMemo(() => {
    if (!selected.length) return "Select inbound group(s)…";
    const names = selected.map((id) => byId.get(id)?.name || id);
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [selected, byId]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3"
        >
          <span className="truncate">{display}</span>
          <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search inbound groups…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>
              {emptyHint || "No inbound groups — create them under Routing → Inbound Groups."}
            </CommandEmpty>
            <CommandGroup>
              {(groups || []).map((g) => {
                const on = selectedSet.has(g.id);
                return (
                  <CommandItem
                    key={g.id}
                    value={`${g.name} ${g.id}`}
                    data-testid={`${testid}-option-${g.id}`}
                    onSelect={() => toggle(g.id)}
                  >
                    <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${on ? "opacity-100" : "opacity-0"}`} />
                    <span className="truncate">{g.name}</span>
                    {g.status !== "active" && (
                      <span className="ml-auto text-[10px] uppercase text-muted-foreground">inactive</span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function InboundGroupsField({
  prefix, groups, selectedIds, onChange, onReload, optionsError,
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Inbound group name is required");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/customer-routing/inbound-groups", {
        name: trimmed,
        description: description.trim() || null,
        status: "active",
      });
      const id = data?.id;
      if (id && !selectedIds.includes(id)) onChange([...selectedIds, id]);
      toast.success("Inbound group added");
      setName("");
      setDescription("");
      setAdding(false);
      await onReload?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Field
      label="Inbound groups"
      hint="Select existing groups or add a new one. The same group can be used on multiple campaigns."
      full
    >
      <div className="flex gap-2">
        <div className="flex-1 min-w-0">
          <InboundGroupsMultiSelect
            groups={groups}
            selectedIds={selectedIds}
            onChange={onChange}
            testid={tid(prefix, "inbound-groups")}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          className="shrink-0 h-9"
          onClick={() => setAdding((v) => !v)}
          data-testid={tid(prefix, "inbound-group-add")}
        >
          <Plus className="w-4 h-4 mr-1" weight="bold" />
          Add
        </Button>
      </div>
      {adding && (
        <div
          className="mt-2 rounded-lg border border-border bg-muted/30 p-3 space-y-2"
          data-testid={tid(prefix, "inbound-group-add-form")}
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Inbound group name"
            data-testid={tid(prefix, "inbound-group-new-name")}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                create();
              }
            }}
          />
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            data-testid={tid(prefix, "inbound-group-new-description")}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={create}
              disabled={saving}
              data-testid={tid(prefix, "inbound-group-new-save")}
            >
              {saving ? "Adding…" : "Add inbound group"}
            </Button>
          </div>
        </div>
      )}
      {groups.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground" data-testid={tid(prefix, "inbound-groups-empty")}>
          No inbound groups yet — click Add to create one.
        </p>
      )}
      {optionsError && (
        <p className="text-xs text-destructive">{optionsError}</p>
      )}
    </Field>
  );
}

/** Titled group of related fields. Rendered only when it has fields to show. */
function Section({ title, description, testid, children }) {
  return (
    <section className="rounded-lg border border-border overflow-hidden bg-white shadow-sm" data-testid={testid}>
      <div className="border-b border-border bg-white px-4 py-2.5">
        <h3 className="text-sm font-semibold leading-none">{title}</h3>
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </div>
      <div className="grid gap-4 p-4 sm:grid-cols-2 bg-white">{children}</div>
    </section>
  );
}

/** One labelled field. `full` spans both columns for wide inputs and lists. */
function Field({ label, required, hint, full = false, children }) {
  return (
    <div className={`space-y-2 ${full ? "sm:col-span-2" : ""}`}>
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** Switch on its own bordered row, for settings that read as a sentence. */
function ToggleRow({ title, description, checked, onCheckedChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-md border px-3 py-2 sm:col-span-2">
      <div>
        <Label className="text-sm">{title}</Label>
        {description && <p className="text-[11px] text-muted-foreground">{description}</p>}
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

function AmdBlock({
  mode, prefix, v, set, vmUploading, onVmUploadClick, vmInputRef, onVmFileChange,
}) {
  return (
    <div className="space-y-3 rounded-lg border border-border p-3 sm:col-span-2">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm">Answering Machine Detection</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Campaign fields tune Layer 2 / wait budgets only. FreeSWITCH Layer 1
            uses the global media-host amd.conf.xml (not these values).
          </p>
        </div>
        <Switch
          data-testid={mode === "edit" ? "edit-campaign-amd-enabled" : "create-campaign-amd-enabled"}
          checked={!!v.amdEnabled}
          onCheckedChange={(x) => set({ amdEnabled: x })}
        />
      </div>
      {v.amdEnabled && (
        <div className="grid grid-cols-2 gap-3 pt-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Decision window (ms) <span className="text-muted-foreground font-normal">· L1 wait budget</span></Label>
            <Input
              data-testid={mode === "edit" ? "edit-campaign-amd-timeout" : "create-campaign-amd-timeout"}
              type="number" min="1000" max="8000" step="100"
              value={v.amdTimeout || "3200"}
              onChange={(e) => set({ amdTimeout: e.target.value })}
              title="How long we wait for mod_amd — does not change amd.conf.xml"
            />
            <p className="text-[10px] text-muted-foreground">Does not change FreeSWITCH L1 algorithm params.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">On voicemail <span className="text-muted-foreground font-normal">· policy</span></Label>
            <Select value={v.amdAction || "hangup"} onValueChange={(x) => set({ amdAction: x })}>
              <SelectTrigger data-testid={mode === "edit" ? "edit-campaign-amd-action" : "create-campaign-amd-action"}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hangup">Hang up</SelectItem>
                <SelectItem value="voicemail">Leave voicemail</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground">Action after AMD classifies machine — not L1 tuning.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Voicemail Max (ms) <span className="text-muted-foreground font-normal">· Layer 2 only</span></Label>
            <Input
              data-testid={mode === "edit" ? "edit-campaign-amd-greeting-max" : "create-campaign-amd-greeting-max"}
              type="number" min="2000" max="12000" step="100"
              value={v.amdGreetingMax || "4500"}
              onChange={(e) => set({ amdGreetingMax: e.target.value })}
              title="Layer 2 greeting length threshold only"
            />
            <p className="text-[10px] text-muted-foreground">Python Layer 2 classifier only — not mod_amd / amd.conf.xml.</p>
          </div>
          {v.amdAction === "voicemail" && (
            <div className="col-span-2 space-y-1.5 rounded-md border border-dashed border-border p-2.5">
              <Label className="text-xs">Voicemail drop audio <span className="text-destructive">*</span></Label>
              <input
                ref={vmInputRef}
                type="file"
                accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm"
                hidden
                onChange={onVmFileChange}
                data-testid={mode === "edit" ? "edit-campaign-vm-file-input" : "create-campaign-vm-file-input"}
              />
              <div className="flex items-center gap-2">
                <Button
                  type="button" variant="outline" size="sm" className="rounded-lg"
                  disabled={vmUploading}
                  onClick={onVmUploadClick}
                  data-testid={mode === "edit" ? "edit-campaign-vm-upload-btn" : "create-campaign-vm-upload-btn"}
                >
                  {vmUploading ? "Uploading…" : (v.vmAudioId ? "Replace audio" : "Upload audio")}
                </Button>
                {v.vmAudioId
                  ? (
                    <span
                      className="text-xs text-emerald-600 truncate"
                      data-testid={mode === "edit" ? "edit-campaign-vm-filename" : "create-campaign-vm-filename"}
                    >
                      {v.vmFilename || "Voicemail audio attached"}
                    </span>
                    )
                  : <span className="text-xs text-muted-foreground">No file uploaded yet</span>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CampaignFormFields({
  mode = "create",
  value,
  onChange,
  options = {},
  vmUploading = false,
  onVmUploadClick,
  vmInputRef,
  onVmFileChange,
  optionsError,
  onInboundGroupsReload,
  hideDialingType = false,
}) {
  const prefix = mode === "edit" ? "edit-campaign" : "campaign";
  const v = value || {};
  const set = (patch) => onChange(patch);
  const vis = fieldVisibility(v.dialingType, v.campaignType);
  const {
    pdServers = [],
    vicidial = [],
    assigned = [],
    mediaServers = [],
    inboundGroups = [],
    userGroups = [],
    leadLists = [],
    vendors = [],
    timezones = [],
  } = options;

  const igVisible = visibleInboundGroups(inboundGroups, v.inboundGroupIds);

  const toggleId = (key, id, checked) => {
    const cur = Array.isArray(v[key]) ? v[key] : [];
    set({ [key]: checked ? [...cur, id] : cur.filter((x) => x !== id) });
  };

  const amdProps = {
    mode, prefix, v, set, vmUploading, onVmUploadClick, vmInputRef, onVmFileChange,
  };

  // A section is skipped entirely when the dialing type hides all of its fields.
  const hasAgents = vis.agentSession || vis.userGroups || vis.leadLists || vis.agentCallRouting;
  const hasDialing = vis.dialLevel || vis.dialRate || vis.dialStatuses || vis.callback;
  const hasRouting = vis.vicidial || vis.pdServer || vis.telephonyIp || vis.mediaServer
    || vis.vendor || vis.callerId || vis.inboundGroups;
  const opensipsIds = selectedOpensipsIds(v);
  const mediaIds = selectedMediaServerIds(v);
  const setOpensipsIds = (ids) => set({ opensipsIds: ids, opensipsId: ids[0] || "__none__" });
  const setMediaIds = (ids) => set({ mediaServerIds: ids, mediaServerId: ids[0] || "" });
  const hasHandling = vis.amd || vis.recording || vis.maxDuration;

  return (
    <div className="space-y-4" data-testid={tid(prefix, "form")}>
      {/* ── Campaign ──────────────────────────────────────────── */}
      <Section
        title="Campaign"
        description="Name the campaign and choose how it dials."
        testid={tid(prefix, "section-campaign")}
      >
        <Field label="Campaign name" full>
          <Input
            data-testid={tid(prefix, "name")}
            value={v.name || ""}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="e.g. Sales Outbound"
          />
        </Field>

        {!hideDialingType && vis.dialingType && (
          <Field label="Dialing type">
            <Select value={v.dialingType} onValueChange={(x) => set({ dialingType: x })}>
              <SelectTrigger data-testid={tid(prefix, "dialing-type")}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ai_bot">VICI Bot</SelectItem>
                <SelectItem value="predictive">Predictive</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="bot">AI Agent</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        )}

        {vis.campaignType && (
          <Field label="Campaign type">
            <Select
              value={v.campaignType}
              onValueChange={(x) => set({ campaignType: x })}
            >
              <SelectTrigger data-testid={tid(prefix, "campaign-type")}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="outbound">Outbound</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="blended">Blended</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        )}

        {vis.dialingPrefix && (
          <Field
            label="Dialing prefix"
            hint="Using this digit agents can directly dial from their softphone. Leave empty to disable. The matched prefix uses this campaign's vendor and is removed before the number is sent to the vendor."
          >
            <Input
              data-testid={tid(prefix, "dialing-prefix")}
              inputMode="numeric"
              maxLength={8}
              value={v.dialingPrefix || ""}
              onChange={(e) => set({
                dialingPrefix: String(e.target.value || "").replace(/\D/g, "").slice(0, 8),
              })}
              placeholder="e.g. 9"
            />
          </Field>
        )}
      </Section>

      {/* ── Agents & leads ────────────────────────────────────── */}
      {hasAgents && (
        <Section
          title="Agents & leads"
          description="Who takes the calls, and which leads are dialed to them."
          testid={tid(prefix, "section-agents")}
        >
          {vis.agentSession && (
            <Field label="Agent Session">
              <Select
                value={normalizeAgentSession(v.agentSession)}
                onValueChange={(x) => set({ agentSession: x })}
              >
                <SelectTrigger data-testid={tid(prefix, "agent-session")}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="conference">Conference</SelectItem>
                  <SelectItem value="direct_phone">Direct phone</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          {vis.agentCallRouting && (
            <Field
              label="Agent Call Routing"
              hint="How logged-in Ready agents receive the next call."
            >
              <Select
                value={normalizeAgentCallRouting(v.agentCallRouting)}
                onValueChange={(x) => set({
                  agentCallRouting: x,
                  agentLevelRouting: x === "agent_level"
                    ? (normalizeAgentLevelRouting(v.agentLevelRouting, x) || "highest")
                    : "",
                })}
              >
                <SelectTrigger data-testid={tid(prefix, "agent-call-routing")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="random">Random</SelectItem>
                  <SelectItem value="less_calls">Less Calls</SelectItem>
                  <SelectItem value="high_calls">High calls</SelectItem>
                  <SelectItem value="agent_level">Agent level</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          {vis.agentCallRouting && normalizeAgentCallRouting(v.agentCallRouting) === "agent_level" && (
            <Field
              label="Agent level"
              hint="If some logged-in agents do not have Agent Level set, those agents are routed randomly."
            >
              <Select
                value={normalizeAgentLevelRouting(v.agentLevelRouting, "agent_level") || "highest"}
                onValueChange={(x) => set({ agentLevelRouting: x })}
              >
                <SelectTrigger data-testid={tid(prefix, "agent-level-routing")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="highest">Highest level</SelectItem>
                  <SelectItem value="lowest">Lowest level</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          {vis.userGroups && (
            <Field label="User groups">
              <div className="rounded-md border p-2.5 max-h-36 overflow-y-auto space-y-1.5" data-testid={tid(prefix, "agent-groups")}>
                {userGroups.map((g) => (
                  <label key={g.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(v.agentGroupIds || []).includes(g.id)}
                      onChange={(e) => toggleId("agentGroupIds", g.id, e.target.checked)}
                    />
                    {g.name}
                  </label>
                ))}
                {userGroups.length === 0 && (
                  <p className="text-xs text-muted-foreground">No user groups — create them under Users &amp; Groups.</p>
                )}
              </div>
            </Field>
          )}

          {vis.leadLists && (
            <Field label="Lead lists">
              <div className="rounded-md border p-2.5 max-h-36 overflow-y-auto space-y-1.5" data-testid={tid(prefix, "lead-lists")}>
                {leadLists.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(v.listIds || []).includes(l.id)}
                      onChange={(e) => toggleId("listIds", l.id, e.target.checked)}
                    />
                    {l.name}
                    {l.lead_count != null && (
                      <span className="text-xs text-muted-foreground">({l.lead_count})</span>
                    )}
                  </label>
                ))}
                {leadLists.length === 0 && (
                  <p className="text-xs text-muted-foreground">No lists yet — create them under Lists.</p>
                )}
              </div>
            </Field>
          )}
        </Section>
      )}

      {/* ── Dialing ───────────────────────────────────────────── */}
      {hasDialing && (
        <Section
          title="Dialing"
          description="Pacing, which lead statuses are called, and callbacks."
          testid={tid(prefix, "section-dialing")}
        >
          {vis.dialLevel && (
            <Field label="Dial level (lines per ready agent)">
              <Input
                type="number" min="0.1" step="0.1"
                value={v.dialLevel || ""}
                onChange={(e) => set({ dialLevel: e.target.value })}
                placeholder="e.g. 1.5"
                data-testid={tid(prefix, "dial-level")}
              />
            </Field>
          )}

          {vis.dialRate && (
            <Field label="Dial rate (calls per second)">
              <Input
                type="number" min="0.1" step="0.1"
                value={v.dialRate || ""}
                onChange={(e) => set({ dialRate: e.target.value })}
                placeholder="e.g. 2"
                data-testid={mode === "edit" ? "edit-campaign-bot-cps" : "campaign-bot-cps"}
              />
            </Field>
          )}

          {vis.dialStatuses && (
            <Field label="Dial statuses" hint="Only leads in these statuses are eligible to dial. DNC is never shown." full>
              <div className="rounded-md border p-2.5 grid grid-cols-2 gap-1.5 sm:grid-cols-3" data-testid={tid(prefix, "dial-statuses")}>
                {DIAL_STATUS_OPTIONS.filter((s) => s !== "DNC").map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={(v.dialStatuses || []).includes(s)}
                      onChange={(e) => toggleId("dialStatuses", s, e.target.checked)}
                    />
                    {s}
                  </label>
                ))}
              </div>
            </Field>
          )}

          {vis.callback && (
            <ToggleRow
              title="Callback scheduling"
              description="Allow scheduled callbacks distinct from automatic retries."
              checked={v.callbackEnabled !== false}
              onCheckedChange={(x) => set({ callbackEnabled: x })}
              testid={tid(prefix, "callback-enabled")}
            />
          )}
        </Section>
      )}

      {/* ── Schedule (hidden for VICI Bot) ─────────────────────── */}
      {vis.schedule && (
        <Section
          title="Schedule"
          description="When this campaign is allowed to run."
          testid={tid(prefix, "section-schedule")}
        >
          <Field
            label="Timezone"
            hint="Schedule times use this timezone (or account default)."
            full
          >
            <TimezoneCombobox
              value={v.timezone || ""}
              onChange={(x) => set({ timezone: x })}
              timezones={timezones}
              testid={tid(prefix, "timezone")}
            />
          </Field>
          <Field label="Schedule start (HH:MM)">
            <Input
              type="time"
              value={v.scheduleStart || ""}
              onChange={(e) => set({ scheduleStart: e.target.value })}
              data-testid={tid(prefix, "schedule-start")}
            />
          </Field>
          <Field label="Schedule end (HH:MM)">
            <Input
              type="time"
              value={v.scheduleEnd || ""}
              onChange={(e) => set({ scheduleEnd: e.target.value })}
              data-testid={tid(prefix, "schedule-end")}
            />
          </Field>
          <p className="text-xs text-muted-foreground sm:col-span-2" data-testid={tid(prefix, "schedule-247-hint")}>
            Leave both blank to run 24×7. Outside the window the campaign cannot start and an active campaign is auto-stopped.
          </p>
        </Section>
      )}

      {/* ── Routing & carriers ────────────────────────────────── */}
      {hasRouting && (
        <Section
          title="Routing & carriers"
          description="Servers and trunks this campaign's calls travel through."
          testid={tid(prefix, "section-routing")}
        >
          {/* VICI Bot: VICIdial + Telephony IP + Media server on one row */}
          {vis.vicidial && (
            <div className="sm:col-span-2 grid gap-4 sm:grid-cols-3">
              <Field label="VICIdial server" required>
                <Select value={v.vicidialId || ""} onValueChange={(x) => set({ vicidialId: x })}>
                  <SelectTrigger data-testid={tid(prefix, "vicidial-select")}><SelectValue placeholder="Select VICIdial" /></SelectTrigger>
                  <SelectContent>
                    {vicidial.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name} · {s.host}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              {vis.telephonyIp && (
                <Field
                  label="Telephony IP"
                  required
                  hint="First selected is Primary; others are backups."
                >
                  <FailoverServerMultiSelect
                    servers={assigned}
                    selectedIds={opensipsIds}
                    onChange={setOpensipsIds}
                    testid={tid(prefix, "opensips-select")}
                    emptyText="No Telephony IP servers assigned to this account."
                  />
                </Field>
              )}
              {vis.mediaServer && (
                <Field
                  label="Media server"
                  required
                  hint="First selected is Primary; others are backups."
                >
                  <FailoverServerMultiSelect
                    servers={mediaServers}
                    selectedIds={mediaIds}
                    onChange={setMediaIds}
                    testid={tid(prefix, "media-select")}
                    emptyText="No media servers assigned to this account."
                  />
                </Field>
              )}
            </div>
          )}

          {/* Non–VICI Bot: Telephony + Media when VICIdial row is not shown */}
          {!vis.vicidial && vis.telephonyIp && (
            <Field
              label="Telephony IP"
              required
              hint="First selected is Primary; others are backups if the primary is down or at capacity."
            >
              <FailoverServerMultiSelect
                servers={assigned}
                selectedIds={opensipsIds}
                onChange={setOpensipsIds}
                testid={tid(prefix, "opensips-select")}
                emptyText="No Telephony IP servers assigned to this account."
              />
            </Field>
          )}
          {!vis.vicidial && vis.mediaServer && (
            <Field
              label="Media server"
              required
              hint="First selected is Primary; others are backups if the primary is down or at capacity."
            >
              <FailoverServerMultiSelect
                servers={mediaServers}
                selectedIds={mediaIds}
                onChange={setMediaIds}
                testid={tid(prefix, "media-select")}
                emptyText="No media servers assigned to this account."
              />
            </Field>
          )}

          {vis.pdServer && (
            <Field
              label="PD servers"
              required={pdServers.length !== 1}
              hint="Select in failover order. If the first server is down or at its call limit, the next selected server is used."
            >
              <FailoverServerMultiSelect
                servers={pdServers}
                selectedIds={
                  (v.pdServerIds || []).length
                    ? v.pdServerIds
                    : (v.pdServerId ? [v.pdServerId] : [])
                }
                onChange={(ids) => set({ pdServerIds: ids, pdServerId: ids[0] || "" })}
                testid={tid(prefix, "pd-select")}
                emptyText="No PD servers assigned to this account."
              />
            </Field>
          )}

          {vis.vendor && (
            <Field
              label="Vendor route"
              required
              hint="Required. Campaign-selected vendor only — no outbound vendor failover."
            >
              <Select
                value={v.vendorId && v.vendorId !== "__none__" ? v.vendorId : undefined}
                onValueChange={(x) => set({ vendorId: x })}
              >
                <SelectTrigger data-testid={tid(prefix, "vendor")}>
                  <SelectValue placeholder="Select vendor route…" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((x) => (
                    <SelectItem key={x.id} value={x.id}>{x.name} · {x.host}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {vendors.length === 0 && (
                <p className="text-xs text-destructive">
                  No active vendor routes — create one under Routing → Vendors.
                </p>
              )}
            </Field>
          )}

          {vis.callerId && (
            <Field label={<>Caller ID <span className="text-muted-foreground font-normal">(optional)</span></>}>
              <Input
                value={v.callerId || ""}
                onChange={(e) => set({ callerId: e.target.value })}
                placeholder="Outbound CID"
                data-testid={tid(prefix, "caller-id")}
              />
            </Field>
          )}

          {vis.inboundGroups && (
            <InboundGroupsField
              prefix={prefix}
              groups={igVisible}
              selectedIds={v.inboundGroupIds || []}
              onChange={(ids) => set({ inboundGroupIds: ids })}
              onReload={onInboundGroupsReload}
              optionsError={optionsError?.inboundGroups}
            />
          )}
        </Section>
      )}

      {/* ── Call handling ─────────────────────────────────────── */}
      {hasHandling && (
        <Section
          title="Call handling"
          description="Machine detection, recording and duration limits."
          testid={tid(prefix, "section-handling")}
        >
          {vis.amd && <AmdBlock {...amdProps} />}

          {vis.recording && (
            <Field
              label="Call recording"
              hint="Never: off. Always: record when the customer is connected. On demand: agent starts recording."
            >
              <Select
                value={v.callRecording || "never"}
                onValueChange={(x) => set({
                  callRecording: x,
                  ...(x !== "always" ? { recordingStartDelaySec: "" } : {}),
                })}
              >
                <SelectTrigger data-testid={tid(prefix, "recording")}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never</SelectItem>
                  <SelectItem value="always">Always</SelectItem>
                  <SelectItem value="on_demand">On demand</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}

          {vis.recording && (v.callRecording || "never") === "always" && (
            <Field
              label="Start Recording after (sec)"
              hint="Leave empty to start recording as soon as the call is connected."
            >
              <Input
                data-testid={tid(prefix, "recording-start-delay")}
                type="number"
                min="1"
                max="3600"
                value={v.recordingStartDelaySec || ""}
                onChange={(e) => set({ recordingStartDelaySec: e.target.value })}
                placeholder=""
              />
            </Field>
          )}

          <Field
            label="Max Call Duration (sec)"
            hint="Caps the AI bot ↔ customer call. Leave blank for no limit. Not applied after transfer to an agent."
          >
            <Input
              data-testid={tid(prefix, "max-duration")}
              type="number" min="1" max="3600"
              value={v.maxDur || ""}
              onChange={(e) => set({ maxDur: e.target.value })}
              placeholder="Unlimited"
            />
          </Field>
        </Section>
      )}
    </div>
  );
}
